/* eslint-disable no-underscore-dangle */
/**
 * Переиспользуемый компонент для поиска фич по слоям
 * Можно использовать для любого слоя с поддержкой поиска
 *
 * Пример использования:
 * <LayerFeatureSearch
 *     searchFn={searchFunctionalAreaInTiles}
 *     highlightFn={highlightFunctionalArea}
 *     clearFn={clearFunctionalAreaHighlight}
 *     placeholder={t`ID зоны`}
 *     notFoundMessage={t`Функциональная зона не найдена`}
 *     successLabel={t`Найдена`}
 *     searchLabel={t`Найти`}
 *     resultFields={[ 'id', 'type' ]}
 * />
 */

import React, { useState } from 'react';
import { t } from '@lingui/macro';
import PropTypes from 'prop-types';

const LayerFeatureSearch = ({
    searchFn,
    highlightFn,
    clearFn,
    placeholder = t`Поиск`,
    notFoundMessage = t`Объект не найден в загруженных данных`,
    successLabel = t`Найден`,
    searchLabel = t`Найти`,
    resultFields = [ 'id' ],
    errorMessage = t`Ошибка поиска`,
    mapNotLoadedMessage = t`Карта не загружена`,
}) => {
    const [ searchValue, setSearchValue ] = useState('');
    const [ searchResult, setSearchResult ] = useState(null);
    const [ searchError, setSearchError ] = useState('');
    const [ isSearching, setIsSearching ] = useState(false);
    const handleSearch = e => {
        e.preventDefault();
        setIsSearching(true);
        setSearchError('');
        setSearchResult(null);

        try {
            if (!window.__map) {
                setSearchError(mapNotLoadedMessage);
                setIsSearching(false);
                return;
            }

            const result = searchFn(window.__map, searchValue.trim());

            if (result) {
                setSearchResult(result);
                setSearchError('');
                highlightFn(window.__map, result);
            } else {
                setSearchError(notFoundMessage);
                clearFn(window.__map);
            }
        } catch (err) {
            setSearchError(errorMessage);
        } finally {
            setIsSearching(false);
        }
    };
    const handleClear = () => {
        setSearchValue('');
        setSearchResult(null);
        setSearchError('');
        if (window.__map) {
            clearFn(window.__map);
        }
    };
    // Форматируем результат для отображения
    const formatResult = () => {
        if (!searchResult) return '';
        // eslint-disable-next-line no-prototype-builtins
        return resultFields
            .filter(field => searchResult.hasOwnProperty(field))
            .map(field => `${field}: ${searchResult[field]}`)
            .join(', ');
    };

    return (
        <div
            style={{
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #ddd',
            }}
        >
            <form
                onSubmit={handleSearch}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                }}
            >
                <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <input
                            type="text"
                            placeholder={placeholder}
                            value={searchValue}
                            onChange={e => setSearchValue(e.target.value)}
                            disabled={isSearching}
                            style={{
                                width: '100%',
                                padding: '8px 32px 8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '13px',
                                outline: 'none',
                                transition: 'border-color 0.2s ease',
                                boxSizing: 'border-box',
                            }}
                            onFocus={e => {
                                e.target.style.borderColor = '#007bff';
                            }}
                            onBlur={e => {
                                e.target.style.borderColor = '#ccc';
                            }}
                        />
                        {searchValue && (
                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={isSearching}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    color: '#999',
                                    padding: '0',
                                    width: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onMouseEnter={e => {
                                    e.target.style.color = '#666';
                                }}
                                onMouseLeave={e => {
                                    e.target.style.color = '#999';
                                }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <button
                        type="submit"
                        disabled={!searchValue.trim() || isSearching}
                        style={{
                            padding: '8px 14px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: !searchValue.trim() || isSearching ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            opacity: !searchValue.trim() || isSearching ? 0.6 : 1,
                            transition: 'opacity 0.2s ease',
                            whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => {
                            if (searchValue.trim() && !isSearching) {
                                e.target.style.backgroundColor = '#0056b3';
                            }
                        }}
                        onMouseLeave={e => {
                            e.target.style.backgroundColor = '#007bff';
                        }}
                    >
                        {isSearching ? t`...` : searchLabel}
                    </button>
                </div>

                {searchError && (
                    <div
                        style={{
                            color: '#721c24',
                            fontSize: '12px',
                            padding: '6px 10px',
                            backgroundColor: '#f8d7da',
                            border: '1px solid #f5c6cb',
                            borderRadius: '3px',
                        }}
                    >
                        {searchError}
                    </div>
                )}

                {searchResult && !searchError && (
                    <div
                        style={{
                            color: '#155724',
                            fontSize: '12px',
                            padding: '6px 10px',
                            backgroundColor: '#d4edda',
                            border: '1px solid #c3e6cb',
                            borderRadius: '3px',
                        }}
                    >
                        <strong>✓ {successLabel}:</strong>
                        {' '}
                        {formatResult()}
                    </div>
                )}
            </form>
        </div>
    );
};

LayerFeatureSearch.propTypes = {
    /** Функция поиска (map, searchValue) => result */
    searchFn: PropTypes.func.isRequired,
    /** Функция выделения (map, result) => void */
    highlightFn: PropTypes.func.isRequired,
    /** Функция очистки выделения (map) => void */
    clearFn: PropTypes.func.isRequired,
    /** Placeholder input поля */
    placeholder: PropTypes.string,
    /** Сообщение если не найдено */
    notFoundMessage: PropTypes.string,
    /** Лейбл успешного поиска */
    successLabel: PropTypes.string,
    /** Текст кнопки поиска */
    searchLabel: PropTypes.string,
    /** Поля для отображения результата */
    resultFields: PropTypes.arrayOf(PropTypes.string),
    /** Сообщение об ошибке поиска */
    errorMessage: PropTypes.string,
    /** Сообщение если карта не загружена */
    mapNotLoadedMessage: PropTypes.string,
};

export default LayerFeatureSearch;
